import React from 'react';
import { 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Typography, 
  CircularProgress,
  FormHelperText,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/apiService';
import { Practice, Workflow } from '../types/api';

interface PracticeWorkflowSelectorProps {
  onPracticeChange: (practiceId: string) => void;
  onWorkflowChange: (workflowIds: string[]) => void;
  selectedPractice: string;
  selectedWorkflows: string[];
  disabled?: boolean;
}

export default function PracticeWorkflowSelector({
  onPracticeChange,
  onWorkflowChange,
  selectedPractice,
  selectedWorkflows,
  disabled = false
}: PracticeWorkflowSelectorProps) {
  // Get all practices
  const { data: practices, isLoading: practicesLoading } = useQuery({
    queryKey: ['practices'],
    queryFn: () => apiService.getPractices()
  });

  // Get workflows for selected practice
  const { data: workflows, isLoading: workflowsLoading } = useQuery({
    queryKey: ['workflows', selectedPractice],
    queryFn: () => apiService.getWorkflowsForPractice(selectedPractice),
    enabled: !!selectedPractice
  });

  // Handle practice change
  const handlePracticeChange = (event: any) => {
    const practiceId = event.target.value as string;
    onPracticeChange(practiceId);
    onWorkflowChange([]); // Reset workflows when practice changes
  };

  // Handle workflow selection
  const handleWorkflowChange = (workflowId: string) => {
    if (selectedWorkflows.includes(workflowId)) {
      onWorkflowChange(selectedWorkflows.filter(id => id !== workflowId));
    } else {
      onWorkflowChange([...selectedWorkflows, workflowId]);
    }
  };

  const isLoading = practicesLoading || workflowsLoading;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Select Practice and Workflow
      </Typography>

      {isLoading ? (
        <CircularProgress />
      ) : (
        <>
          <FormControl fullWidth sx={{ mb: 3 }} disabled={disabled}>
            <InputLabel>Practice Area</InputLabel>
            <Select
              value={selectedPractice}
              onChange={handlePracticeChange}
              label="Practice Area"
            >
              {practices?.map((practice: Practice) => (
                <MenuItem key={practice.id} value={practice.id}>
                  {practice.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Select the legal practice area</FormHelperText>
          </FormControl>

          {selectedPractice && workflows && workflows.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Available Workflows:
              </Typography>
              <Grid container spacing={2}>
                {workflows.map((workflow: Workflow) => (
                  <Grid item xs={12} sm={6} key={workflow.id}>
                    <Card 
                      sx={{ 
                        cursor: disabled ? 'default' : 'pointer',
                        border: selectedWorkflows.includes(workflow.id) ? 2 : 1,
                        borderStyle: 'solid',
                        borderColor: selectedWorkflows.includes(workflow.id) ? 'primary.main' : 'divider',
                        opacity: disabled ? 0.7 : 1
                      }}
                      onClick={() => !disabled && handleWorkflowChange(workflow.id)}
                    >
                      <CardContent>
                        <Typography variant="h6">
                          {workflow.ui?.displayName || workflow.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {workflow.ui?.description || workflow.description}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                          Accepts: {workflow.fileTypes?.allowed_input?.join(', ').toUpperCase() || 
                                   workflow.requiredFileTypes?.map(type => {
                                     if (type === 'application/pdf') return 'PDF';
                                     if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'DOCX';
                                     if (type === 'text/plain') return 'TXT';
                                     if (type === 'application/json') return 'JSON';
                                     return type;
                                   }).join(', ') || 'PDF, DOCX'}
                        </Typography>
                        {workflow.ui?.helpText && (
                          <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                            {workflow.ui.helpText}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}